import {KnowledgeObject} from "../../../domain/entities/knowledge-object.entity";
import {KnowledgeObjectType} from "../../../domain/entities/knowledge-object-type.type";
import {KnowledgeObjectRepository} from "../../../domain/repositories/knowledge-object.repository";
import {CommandBus} from "../../../services/command-bus.service";
import {UpdateKnowledgeObjectMetadataCommand} from "../../../commands/update-knowledge-object-metadata.command";
import {AssetUploadResult} from "../../../connectivity/asset-upload-result.type";
import {buildCreateUrl, isRenameMode} from "./knowledge-object-edit-mode.helper";

/**
 * Minimal dependency surface for the submit handler — kept narrow so the
 * function is trivially testable (no DI container at the call site).
 */
export interface SubmitDeps {
    readonly knowledgeObjectRepository: Pick<KnowledgeObjectRepository, 'create' | 'saveContent'>;
    readonly commandBus: Pick<CommandBus, 'dispatchCommand'>;
    readonly navigate: (url: string) => void;
    /** Streams the picked file to `POST /api/v1/files`. */
    readonly uploadFile: (file: File) => Promise<AssetUploadResult>;
    /**
     * Compensating rollback for a KnowledgeObject whose content could not be
     * written. Injected as a narrow seam rather than taken from the repository
     * so this helper stays independent of the delete command chain.
     */
    readonly deleteKnowledgeObject: (knowledgeObject: KnowledgeObject) => Promise<void>;
}

export interface SubmitArgs {
    readonly knowledgeObject: KnowledgeObject | null;
    readonly title: string;
    /** Object type to create (create-mode only; ignored in rename-mode). */
    readonly type?: KnowledgeObjectType;
    /** Picked image file — required when `type` is `image`. */
    readonly file?: File | null;
}

/** Single content item of an `image` KnowledgeObject. */
interface ImageContentItem {
    readonly kind: 'image';
    readonly assetUid: string;
    readonly mime: string;
    readonly sizeBytes: number;
}

/**
 * Routes the dialog submit to either a `create` (with navigation to the
 * new whiteboard detail route) or an `UpdateKnowledgeObjectMetadataCommand`
 * dispatch. Returns the created KnowledgeObject in create-mode, `null` in
 * rename-mode (caller has no use for a return value there).
 */
export async function submitKnowledgeObjectEdit(
    args: SubmitArgs,
    deps: SubmitDeps,
): Promise<KnowledgeObject | null> {
    if (isRenameMode(args.knowledgeObject)) {
        // Rename — dispatch update command keyed by the existing UID.
        await deps.commandBus.dispatchCommand(
            new UpdateKnowledgeObjectMetadataCommand({
                objectUid: args.knowledgeObject!.uid,
                title: args.title,
            })
        );
        return null;
    }

    if (args.type === `image`) {
        return createImageKnowledgeObject(args, deps);
    }

    // Create — delegate to repository (type defaults to whiteboard), then
    // navigate to the type-agnostic detail route (knowledge-view-pane branches
    // on the object's type to render the whiteboard or the rich-text editor).
    const created = await deps.knowledgeObjectRepository.create(args.title, args.type ?? `whiteboard`);
    deps.navigate(buildCreateUrl(created.uid, args.title));
    return created;
}

/**
 * Not reachable from the dialog since 2026-10-16: its type picker no longer offers `image`
 * (Anke). Kept on purpose so the option can come back — see `_typeItems` in the dialog and
 * feature plan 0056.
 *
 * Creates an `image` KnowledgeObject: the bytes are stored FIRST, so a failed
 * upload leaves nothing behind at all. Only once the asset exists is the
 * KnowledgeObject created and its content written; if that content write fails
 * the just-created object is deleted again, because an image KO without content
 * can never render and would show up as a permanently broken row
 * (O2D-TD-094 §5.7).
 */
async function createImageKnowledgeObject(
    args: SubmitArgs,
    deps: SubmitDeps,
): Promise<KnowledgeObject> {
    if (!args.file) {
        throw new Error(`An image KnowledgeObject requires a file.`);
    }

    const asset = await deps.uploadFile(args.file);
    const created = await deps.knowledgeObjectRepository.create(args.title, `image`);

    const content: ImageContentItem = {
        kind: `image`,
        assetUid: asset.uid,
        mime: asset.mime,
        sizeBytes: asset.sizeBytes,
    };

    try {
        await deps.knowledgeObjectRepository.saveContent(created.uid, [content]);
    } catch (error) {
        // Roll the orphan back, but never let a failing rollback mask the
        // original cause — the user must see why the save failed.
        await deps.deleteKnowledgeObject(created).catch(() => { /* keep original error */ });
        throw error;
    }

    deps.navigate(buildCreateUrl(created.uid, args.title));
    return created;
}
